/**
 * Soft-ask de WhatsApp no D0 — decisão PURA, sem React e sem Supabase.
 *
 * ── POR QUE O CHIP NÃO VEM DE UM TOOL DO MODELO ─────────────────────────────
 * A ideia original era um tool novo (`propose_whatsapp_link`) que o Espelho
 * emitiria como os outros. Ler o código derrubou isso por dois motivos que só
 * aparecem no contrato real:
 *
 *  1. TODA description de `_shared/mirror-tools.ts` carrega o mesmo GUARDRAIL:
 *     "NEVER call this without clear user intent expressed in the current
 *     turn", reforçado por few-shots ✅/❌ (a lição do commit c48d4cd). Um
 *     soft-ask, por definição, NÃO tem intenção do usuário no turno — não
 *     existe mensagem que deva dispará-lo. Seria o único tool com o gatilho
 *     invertido, num prompt onde regra textual comprovadamente vaza, e
 *     degradaria a precisão dos outros treze.
 *  2. Um experimento cuja EXPOSIÇÃO é decidida pelo modelo não é legível.
 *     Turno 1, turno 8, nunca ou duas vezes — e o lift de 16,8% → X vira
 *     ruído. A exposição precisa ser determinística para o número significar
 *     alguma coisa.
 *
 * O que a decisão do dono pedia — "o Espelho perguntando DENTRO da conversa,
 * chip inline, não bottom-sheet" — é sobre a SUPERFÍCIE, e está preservado
 * inteiro: o chip renderiza no fio, com a voz do Espelho vindo dos locale
 * files. O que mudou é QUEM decide o instante: o cliente, deterministicamente,
 * em vez do Sonnet.
 */

/** Braço gravado no cadastro. `unassigned` = linha ausente (não deve ocorrer). */
export type WaSoftAskArm = "treatment" | "control" | "pre_experiment" | "unassigned";

export interface WaSoftAskState {
  arm: WaSoftAskArm;
  /** pct vigente NA ATRIBUIÇÃO — separa "controle por sorteio" de "desligado". */
  assignedPct: number;
  /** Quem ganha o primeiro momento do Espelho quando os dois querem falar. */
  ownsD0: boolean;
  alreadyLinked: boolean;
  /** O push soft-ask já foi resolvido ou está em snooze → o momento está livre. */
  pushDecided: boolean;
}

export const FALLBACK_WA_SOFT_ASK: WaSoftAskState = {
  arm: "unassigned",
  assignedPct: 0,
  ownsD0: false,
  alreadyLinked: false,
  pushDecided: false,
};

/** Normaliza o jsonb da RPC. Qualquer lixo → estado que NÃO mostra o chip. */
export function parseWaSoftAskState(raw: unknown): WaSoftAskState {
  if (!raw || typeof raw !== "object") return FALLBACK_WA_SOFT_ASK;
  const r = raw as Record<string, unknown>;
  const arm = r.arm;
  return {
    arm: arm === "treatment" || arm === "control" || arm === "pre_experiment"
      ? arm
      : "unassigned",
    assignedPct: typeof r.assignedPct === "number" && r.assignedPct >= 0
      ? Math.floor(r.assignedPct)
      : 0,
    ownsD0: r.ownsD0 === true,
    alreadyLinked: r.alreadyLinked === true,
    pushDecided: r.pushDecided === true,
  };
}

// `reason?: never` no braço `show: true` NÃO é decoração. O gate de tipo roda
// `tsc -p tsconfig.app.json`, que tem `strict: false` e portanto
// `strictNullChecks: false` — e SEM strictNullChecks o TypeScript não estreita
// união discriminada por booleano. `if (decision.show) return true` não elimina
// o braço `{ show: true }`, e todo `decision.reason` depois disso vira TS2339.
// Sob `strict: true` (o que o editor usa) o mesmo código passa: é exatamente
// por isso que isto nasceu verde na máquina de quem escreveu e vermelho na CI.
// Declarar a propriedade ausente como `never` legaliza o acesso nos dois braços
// sem afrouxar nada: continua impossível construir `{ show: true, reason: … }`.
export type WaSoftAskDecision =
  | { show: true; reason?: never }
  | { show: false; reason: WaSoftAskSkipReason };

export type WaSoftAskSkipReason =
  | "not_treatment"
  | "already_linked"
  | "already_offered_this_session"
  | "push_owns_the_moment"
  | "no_exchange_yet";

export interface WaSoftAskInput {
  state: WaSoftAskState;
  /** A primeira troca completa do turno já aconteceu (mesma condição do push). */
  firstExchangeComplete: boolean;
  /** Já ofertamos nesta sessão — exclusão de SESSÃO, não permanente. */
  offeredThisSession: boolean;
  /**
   * O push soft-ask JÁ CEDEU o momento, nesta sessão, ao vivo.
   *
   * `state.pushDecided` é um retrato tirado na montagem e nunca mais relido —
   * no D0 ele nasce `false` e MORRE `false`, porque a resolução do push
   * acontece depois, na mesma sessão, e a RPC não é refeita. Este campo é o
   * canal que faltava: o componente do push avisa quando terminou (resolveu,
   * adiou ou descobriu que nunca vai conseguir aparecer) e o chip destrava
   * NA HORA, em vez de esperar a próxima sessão que 82,5% dos vínculos não
   * têm — eles acontecem na primeira hora.
   */
  pushSettledThisSession?: boolean;
}

/**
 * Mostra o chip?
 *
 * A ORDEM é a resposta à pergunta "por que não apareceu?", então ela é fixa e
 * testada: do motivo mais estrutural (braço) para o mais circunstancial
 * (momento). Cada `reason` é telemetria, não só controle de fluxo — é o que
 * permite explicar a exposição depois sem adivinhar.
 */
export function decideWaSoftAsk(input: WaSoftAskInput): WaSoftAskDecision {
  const { state, firstExchangeComplete, offeredThisSession } = input;

  // Braço. `control`, `pre_experiment` e `unassigned` nunca veem o chip — e
  // são estados DISTINTOS de propósito: control é sorteio, pre_experiment é
  // quem já existia, unassigned é falha de atribuição. Colapsar os três
  // esconderia exatamente o bug que a gente precisaria enxergar.
  if (state.arm !== "treatment") return { show: false, reason: "not_treatment" };

  // Quem já vinculou não é perguntado. É a métrica primária: perguntar a quem
  // já converteu contamina o numerador e irrita.
  if (state.alreadyLinked) return { show: false, reason: "already_linked" };

  // Exclusão de SESSÃO (decisão travada): quem viu hoje não vê de novo hoje,
  // mas amanhã pode. Não é banimento.
  if (offeredThisSession) {
    return { show: false, reason: "already_offered_this_session" };
  }

  // O ÁRBITRO do primeiro momento.
  //
  // No D0 todo usuário novo tem push_opt_in NULL e nenhum snooze — então o
  // push soft-ask SEMPRE tem direito de aparecer, no MESMO instante. Isso não
  // é caso de borda: é o caso normal, e é o que faria os dois pedidos se
  // matarem. Por padrão o push ganha (converteu 439 e quadruplicou o D1; o
  // chip é hipótese). `ownsD0` inverte isso por config, sem deploy.
  //
  // ⚠ CORRIGIDO — este guard era um DEADLOCK, não uma arbitragem.
  // Medido em produção (PostHog, 29/07–11/08): 27 eventos / 21 pessoas do
  // braço treatment pararam AQUI, e nenhuma passou. A razão é estrutural, não
  // estatística: `pushDecided` vinha de um retrato tirado na montagem, e a
  // única coisa capaz de virá-lo — o usuário responder ao push — acontece
  // DEPOIS, na mesma sessão, sem que ninguém releia a RPC. Ceder a vez virava
  // ceder para sempre.
  //
  // Pior no iOS Safari fora de PWA: `detectIsSupported()` devolve false
  // (useOneSignal.ts:95), o sheet do push nunca abre, `push_opt_in` fica NULL
  // para SEMPRE — e o chip ficava matematicamente inalcançável pelo resto da
  // vida da conta, não só no D0.
  //
  // O contrato certo é "o push tem a VEZ", não "o push tem o DIA":
  // `pushSettledThisSession` é o aviso ao vivo de que ele terminou (resolveu,
  // adiou, ou nunca vai conseguir aparecer) e o chip entra em seguida, na
  // mesma sessão.
  const pushSettled = state.pushDecided || input.pushSettledThisSession === true;
  if (!pushSettled && !state.ownsD0) {
    return { show: false, reason: "push_owns_the_moment" };
  }

  // Mesmo gatilho do push: depois da primeira troca completa. Antes disso o
  // Espelho ainda não disse nada, e um convite sem conversa é um modal com
  // outra roupa.
  if (!firstExchangeComplete) return { show: false, reason: "no_exchange_yet" };

  return { show: true };
}

/**
 * A decisão já é DEFINITIVA, ou ainda pode virar nesta sessão?
 *
 * Existe porque a telemetria mentia por antecipação. O gate disparava UMA vez,
 * no primeiro instante em que havia um braço — quando a resposta ainda era
 * provisória — e gravava `push_owns_the_moment` como se fosse desfecho. Era o
 * que produzia 100% de `shown=false`: não um veredito, um retrato tirado cedo
 * demais.
 *
 * Terminal de verdade:
 *  - `show: true` — apareceu, acabou;
 *  - `not_treatment` / `already_linked` — nada nesta sessão muda isso;
 *  - qualquer bloqueio quando o push JÁ cedeu e a troca JÁ aconteceu — não há
 *    mais evento pendente capaz de destravar.
 *
 * Provisórios (`push_owns_the_moment` antes do push ceder, `no_exchange_yet`
 * antes da troca) não emitem: esperam. Um evento que espera vale mais que um
 * evento que chuta.
 */
export function isWaSoftAskDecisionFinal(
  input: WaSoftAskInput,
  decision: WaSoftAskDecision,
): boolean {
  if (decision.show) return true;
  if (decision.reason === "not_treatment" || decision.reason === "already_linked") return true;
  const pushSettled = input.state.pushDecided || input.pushSettledThisSession === true;
  return pushSettled && input.firstExchangeComplete;
}

/**
 * Dia canônico do soft-ask (America/Sao_Paulo) — a chave da exclusão DIÁRIA.
 *
 * O copy do decline sempre prometeu "volta amanhã"; a implementação era
 * useState, ou seja, "volta na próxima montagem". Um refresh de página
 * reofertava a quem já tinha dito não e cunhava exposição nova — denominador
 * inflado. O dia é em SP como todo dia canônico do produto (mesmo fuso do
 * índice único de check-in e do tasks-sync).
 */
export function waSoftAskDayKey(now: Date): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}
